import { mountViewer } from "./skin.js";
import type { Tour } from "@andarama/schema";
import { parseDeepLink, installDiag, diagMark, diag } from "@andarama/viewer";
import { ensureUiLang } from "./i18n.js";
import { baseFromTourUrl } from "./url.js";
import { launchParams } from "./quiz-result.js";

/**
 * Punto de entrada del bundle standalone: paginas de tour publicadas
 * (/t/{slug}) y paquetes exportados. Lee la configuracion de
 * window.Andarama_CONFIG o de atributos data-* del contenedor #andarama.
 */

interface StandaloneConfig {
  tourUrl?: string;
  tour?: Tour;
  baseUrl?: string;
  analyticsEndpoint?: string | null;
  formEndpoint?: string | null;
  /** Resultado del quiz: solo en el tour servido por la plataforma. */
  quizEndpoint?: string | null;
  turnstileSiteKey?: string | null;
  kiosk?: boolean;
  liveUrl?: string;
  liveRole?: "guide" | "attendee";
  liveKey?: string;
  liveName?: string;
  /** Insignia «Hecho con Andarama» (instancia alojada, planes sin marca blanca). */
  badge?: { url: string };
}

declare global {
  interface Window {
    Andarama_CONFIG?: StandaloneConfig;
    Andarama?: { mount: typeof mountViewer; version: string; instance?: ReturnType<typeof mountViewer> };
  }
}

/** «<versión>+<sha corto> · <fecha>», inyectada por bundle.mjs (esbuild `define`). */
declare const __ANDARAMA_VERSION__: string | undefined;
const VERSION = typeof __ANDARAMA_VERSION__ === "string" ? __ANDARAMA_VERSION__ : "dev";

// La versión no se ve en el visor: queda en window.Andarama.version y en el
// atributo data-version del contenedor, para quien informa de un problema.
window.Andarama = { mount: mountViewer, version: VERSION };


// Diagnóstico del arranque (diag.ts): se instala antes de nada para que un
// fallo en la carga del tour o del idioma también quede registrado.
installDiag({
  app: "viewer",
  slug: /\/t\/([^/?#]+)/.exec(location.pathname)?.[1] ?? "export",
  endpoint: window.Andarama_CONFIG?.analyticsEndpoint ?? null,
  snapshot: () => ({
    loader: document.querySelector(".anda-loading") != null,
    canvas: document.querySelector("#andarama canvas") != null,
  }),
});
diagMark("visor:version", VERSION);

async function boot(): Promise<void> {
  const container = document.getElementById("andarama");
  if (container == null) {
    diagMark("visor:sin-contenedor");
    return;
  }
  container.dataset.version = VERSION;
  const cfg: StandaloneConfig = window.Andarama_CONFIG ?? {};
  const tourUrl = cfg.tourUrl ?? container.dataset.tour;
  let tour = cfg.tour;
  if (tour == null && tourUrl != null) {
    diagMark("tour.json:pide");
    const t0 = performance.now();
    const res = await fetch(tourUrl);
    if (!res.ok) {
      diag()?.error("tour.json", `HTTP ${res.status}`);
      container.textContent = `Error ${res.status} cargando el tour`;
      return;
    }
    const text = await res.text();
    tour = JSON.parse(text) as Tour;
    diagMark("tour.json", { ms: Math.round(performance.now() - t0), kb: Math.round(text.length / 1024), scenes: tour.scenes.length });
  }
  if (tour == null) {
    container.textContent = "Sin configuración de tour";
    return;
  }
  const params = new URLSearchParams(location.search);
  const liveRoom = params.get("live");
  // El cromo habla el idioma del tour (o el que pida el enlace, si el tour lo
  // tiene); si no hay diccionario para él, el del navegador. Se pide antes de
  // montar para que la interfaz no parpadee en inglés.
  const linkLang = parseDeepLink(location.href).lang;
  await ensureUiLang(linkLang != null && tour.meta.langs.includes(linkLang) ? linkLang : tour.meta.defaultLang);
  diagMark("idioma", document.documentElement.lang);
  // Lanzamiento desde un LMS: /api/v1/lti/launch redirige con ?lti= y ?name=
  const lanzamiento = launchParams(location.search);
  // La instancia queda accesible en window.Andarama.instance: los integradores
  // (y las pruebas) pueden pilotar el visor ya montado sin volver a montarlo.
  diagMark("montaje");
  window.Andarama!.instance = mountViewer({
    container,
    tour,
    baseUrl: cfg.baseUrl ?? container.dataset.base ?? baseFromTourUrl(tourUrl),
    analyticsEndpoint: cfg.analyticsEndpoint,
    formEndpoint: cfg.formEndpoint,
    quizEndpoint: cfg.quizEndpoint ?? null,
    ltiLaunchId: lanzamiento.ltiLaunchId,
    participantName: lanzamiento.participantName,
    turnstileSiteKey: cfg.turnstileSiteKey,
    // ?kiosk=1 lo enciende y ?kiosk=0 lo apaga aunque el paquete venga en
    // quiosco: una misma dirección sirve para la pantalla y para la persona.
    kiosk: params.get("kiosk") === "0" ? false : cfg.kiosk === true || params.get("kiosk") === "1",
    badge: cfg.badge,
    live:
      cfg.liveUrl != null || liveRoom != null
        ? {
            url:
              cfg.liveUrl ??
              `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/rt/live/${liveRoom}`,
            role: cfg.liveRole ?? (params.get("guide") != null ? "guide" : "attendee"),
            guideKey: cfg.liveKey ?? params.get("guide") ?? undefined,
            name: cfg.liveName ?? params.get("name") ?? undefined,
          }
        : undefined,
  });
  diagMark("montado");
}

function arrancar(): void {
  boot().catch((err: unknown) => diag()?.error("arranque", err));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", arrancar);
} else {
  arrancar();
}
